import React, { useState, useEffect } from 'react';
import { Photo } from '../api/types';
import { PhotoStreamClient } from '../api/PhotoStreamClient';
import { LoadingSpinner } from './LoadingSpinner';

interface PhotoGridProps {
  photos: Photo[];
  loading?: boolean;
  apiClient: PhotoStreamClient;
  onPhotoClick?: (photo: Photo) => void;
}

interface PhotoItemProps {
  photo: Photo;
  apiClient: PhotoStreamClient;
  onClick?: (photo: Photo) => void;
}

const PhotoItem: React.FC<PhotoItemProps> = ({ photo, apiClient, onClick }) => {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [photoUrl, setPhotoUrl] = useState<string>('');

  useEffect(() => {
    const loadPhotoUrl = async () => {
      try {
        // Automatically request small size for grid thumbnails
        const url = await apiClient.getPhotoViewUrl(photo.s3Key, 'small');
        setPhotoUrl(url);
      } catch (error) {
        setImageError(true);
        setImageLoading(false);
      }
    };

    loadPhotoUrl();
  }, [photo.s3Key, apiClient]);

  return (
    <div 
      className="relative aspect-square bg-muted rounded-lg overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => onClick?.(photo)}
    >
      {imageLoading && !imageError && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
        </div>
      )}
      
      {imageError ? (
        <div className="absolute inset-0 flex items-center justify-center bg-muted">
          <span className="text-sm text-muted-foreground">Failed to load</span>
        </div>
      ) : photoUrl ? (
        <img
          src={photoUrl}
          alt={`Gallery item ${photo.id}`}
          className={`w-full h-full object-cover transition-opacity ${
            imageLoading ? 'opacity-0' : 'opacity-100'
          }`}
          loading="lazy"
          onLoad={() => setImageLoading(false)}
          onError={() => {
            setImageLoading(false);
            setImageError(true);
          }}
        />
      ) : null}
    </div>
  );
};

export const PhotoGrid: React.FC<PhotoGridProps> = ({ 
  photos, 
  loading = false, 
  apiClient,
  onPhotoClick 
}) => {
  if (loading) {
    return <LoadingSpinner />;
  }

  if (photos.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No photos in this gallery yet.</p>
        <p className="text-sm text-muted-foreground mt-2">
          Use the upload button or scan the QR code to add photos.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="photo-grid">
      {photos.map((photo) => (
        <PhotoItem
          key={photo.id}
          photo={photo}
          apiClient={apiClient}
          onClick={onPhotoClick}
        />
      ))}
    </div>
  );
};
